import React, { useMemo } from 'react';

import { Button, Dialog, Grid } from '@mui/material';

import * as TStyle from 'commons/components/Table/Table.styles';
import { PRIMARY_RED } from 'commons/styles/colors';
import { IStockTransfer, IStockTransferItemProps } from 'commons/types';
import { RowTitleProps } from 'commons/types/listPageData.interface';
import { DataTable } from 'pages/dashboard/commons/components';

import S from '../styles/StockTransferDetailDialog.style';

interface BinTransferItemRow {
  key: string;
  inventory_number: string;
  source_bin: string;
  source_quantity: number;
  destination_bin: string;
  destination_quantity: number;
}

const rowTitles: RowTitleProps[] = [
  { title: 'Inventory Number' },
  { title: 'Source Bin' },
  { title: 'Source Qty' },
  { title: 'Destination Bin' },
  { title: 'Destination Qty' },
];

const getNsBinTransferUrl = (binTransferId?: number): string | null => {
  const netsuiteUrl = process.env.REACT_APP_NETSUITE_URL;
  if (!netsuiteUrl || !binTransferId) return null;
  return `${netsuiteUrl}/app/accounting/transactions/bintrnfr.nl?id=${binTransferId}&whence=`;
};

const NetsuiteLink: React.FC<{ label?: string; url: string | null }> = ({
  label,
  url,
}) => {
  if (!label || !url) return <span>{label || '-'}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      {label}
    </a>
  );
};

const groupByInventoryNumber = (
  items: IStockTransferItemProps[],
): Map<string, IStockTransferItemProps[]> =>
  items.reduce((group, item) => {
    group.set(item.inventory_number, [
      ...(group.get(item.inventory_number) ?? []),
      item,
    ]);
    return group;
  }, new Map<string, IStockTransferItemProps[]>());

/**
 * Match source and destination bins of one inventory number by quantity.
 * Each row moves min(remaining source, remaining destination), so one bin can
 * span several rows. Leftover quantity (mismatch) is added to the last row when
 * it comes from the same bin, otherwise it gets its own row against '-'.
 */
const pairBinTransferItems = (
  inventoryNumber: string,
  sources: IStockTransferItemProps[],
  destinations: IStockTransferItemProps[],
): BinTransferItemRow[] => {
  const rows: BinTransferItemRow[] = [];
  const pushRow = (
    row: Omit<BinTransferItemRow, 'key' | 'inventory_number'>,
  ): void => {
    rows.push({
      key: `${inventoryNumber}-${rows.length}`,
      inventory_number: inventoryNumber,
      ...row,
    });
  };

  let sourceIndex = 0;
  let destinationIndex = 0;
  let sourceRemaining = sources[0]?.quantity ?? 0;
  let destinationRemaining = destinations[0]?.quantity ?? 0;

  while (
    sourceIndex < sources.length &&
    destinationIndex < destinations.length
  ) {
    const quantity = Math.min(sourceRemaining, destinationRemaining);
    if (quantity > 0) {
      pushRow({
        source_bin: sources[sourceIndex].bin,
        source_quantity: quantity,
        destination_bin: destinations[destinationIndex].bin,
        destination_quantity: quantity,
      });
    }
    sourceRemaining -= quantity;
    destinationRemaining -= quantity;

    if (sourceRemaining <= 0) {
      sourceIndex += 1;
      sourceRemaining = sources[sourceIndex]?.quantity ?? 0;
    }
    if (destinationRemaining <= 0) {
      destinationIndex += 1;
      destinationRemaining = destinations[destinationIndex]?.quantity ?? 0;
    }
  }

  const addLeftovers = (
    items: IStockTransferItemProps[],
    startIndex: number,
    startRemaining: number,
    isSource: boolean,
  ): void => {
    items.slice(startIndex).forEach((item, offset) => {
      const quantity = offset === 0 ? startRemaining : item.quantity ?? 0;
      const lastRow = rows[rows.length - 1];
      const lastBin = isSource ? lastRow?.source_bin : lastRow?.destination_bin;

      if (offset === 0 && lastRow && lastBin === item.bin) {
        if (isSource) lastRow.source_quantity += quantity;
        else lastRow.destination_quantity += quantity;
        return;
      }

      pushRow({
        source_bin: isSource ? item.bin : '-',
        source_quantity: isSource ? quantity : 0,
        destination_bin: isSource ? '-' : item.bin,
        destination_quantity: isSource ? 0 : quantity,
      });
    });
  };

  addLeftovers(sources, sourceIndex, sourceRemaining, true);
  addLeftovers(destinations, destinationIndex, destinationRemaining, false);

  return rows;
};

const mapBinTransferItemRows = (
  stockTransfer: IStockTransfer | null,
): BinTransferItemRow[] => {
  if (!stockTransfer) return [];

  const sourceGroup = groupByInventoryNumber(stockTransfer.source_bin ?? []);
  const destinationGroup = groupByInventoryNumber(
    stockTransfer.destination_bin ?? [],
  );
  const inventoryNumbers = Array.from(sourceGroup.keys()).concat(
    Array.from(destinationGroup.keys()).filter((key) => !sourceGroup.has(key)),
  );

  return inventoryNumbers.flatMap((inventoryNumber) =>
    pairBinTransferItems(
      inventoryNumber,
      sourceGroup.get(inventoryNumber) ?? [],
      destinationGroup.get(inventoryNumber) ?? [],
    ),
  );
};

const TableRow: React.FC<{
  data: BinTransferItemRow;
  index: number;
}> = ({ data, index }) => {
  const quantityStyle =
    data.source_quantity !== data.destination_quantity
      ? { color: PRIMARY_RED, fontWeight: 'bold' }
      : {};

  return (
    <TStyle.TableRow data-testid={`StockTransferItemRow-${index + 1}`}>
      <TStyle.TableColumn style={{ width: '22%' }}>
        {data.inventory_number || '-'}
      </TStyle.TableColumn>
      <TStyle.TableColumn style={{ width: '24%' }}>
        {data.source_bin}
      </TStyle.TableColumn>
      <TStyle.TableColumn style={{ width: '15%', ...quantityStyle }}>
        {data.source_quantity}
      </TStyle.TableColumn>
      <TStyle.TableColumn style={{ width: '24%' }}>
        {data.destination_bin}
      </TStyle.TableColumn>
      <TStyle.TableColumn style={{ width: '15%', ...quantityStyle }}>
        {data.destination_quantity}
      </TStyle.TableColumn>
    </TStyle.TableRow>
  );
};

export const StockTransferDetailDialog: React.FC<{
  open: boolean;
  stockTransfer: IStockTransfer | null;
  onClose: () => void;
}> = ({ open, stockTransfer, onClose }) => {
  const netsuite = stockTransfer?.related_document?.netsuite;
  const nsBinTransferUrl = getNsBinTransferUrl(netsuite?.bin_transfer_id);
  const itemRows = useMemo(
    () => mapBinTransferItemRows(stockTransfer),
    [stockTransfer],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: { maxWidth: '80vw', width: '80vw', maxHeight: '80vh' },
      }}
    >
      {/* Title */}
      <S.TitleWrapper container>
        <Grid item xs={12}>
          <b>Bin Transfer Detail</b>
        </Grid>
      </S.TitleWrapper>

      {/* Content Document Information */}
      <S.DocumentInformationWrapper container>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Transfer Number</S.DocumentInfoTitle>
          <S.DocumentInfoValue data-testid="stockTransferDetailTransferNumber">
            <b>{stockTransfer?.transfer_number || '-'}</b>
          </S.DocumentInfoValue>
        </Grid>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Replenisher</S.DocumentInfoTitle>
          <S.DocumentInfoValue data-testid="stockTransferDetailReplenisher">
            <b>{stockTransfer?.user || '-'}</b>
          </S.DocumentInfoValue>
        </Grid>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>NS Bin Transfer #</S.DocumentInfoTitle>
          <S.DocumentInfoValue data-testid="stockTransferDetailTranId">
            <b>{netsuite?.tran_id}</b>
          </S.DocumentInfoValue>
        </Grid>
        <Grid item xs={3}>
          <S.DocumentInfoTitle>Netsuite Transaction Number</S.DocumentInfoTitle>
          <S.DocumentInfoValue data-testid="stockTransferDetailTransactionNumber">
            <b>
              <NetsuiteLink
                label={netsuite?.transaction_number}
                url={nsBinTransferUrl}
              />
            </b>
          </S.DocumentInfoValue>
        </Grid>
      </S.DocumentInformationWrapper>

      {/* Bin Transfer Item Table */}
      <S.TableSection>
        <DataTable rowTitles={rowTitles} hideCheckbox>
          <TStyle.TableBody>
            {itemRows.map((item, index) => (
              <TableRow key={item.key} data={item} index={index} />
            ))}
          </TStyle.TableBody>
        </DataTable>
      </S.TableSection>

      <S.ActionWrapper container justifyContent="flex-end">
        <Button onClick={onClose} variant="outlined">
          Close
        </Button>
      </S.ActionWrapper>
    </Dialog>
  );
};
